import React, { useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Avatar,
  Stack,
  Chip,
  IconButton,
  TextField,
  Divider,
  Link,
  Tooltip,
} from '@mui/material';
import {
  Email as EmailIcon,
  Phone as PhoneIcon,
  Business as BusinessIcon,
  Work as WorkIcon,
  Language as LanguageIcon,
  LinkedIn as LinkedInIcon,
  LocationOn as LocationIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { chatApi } from '@/api/chat';
import type { Chat, MatchStrength, ConversationSignalEntry } from '@/types/chat';
import { getConversationSignalUi } from '@/constants/conversationSignalsCatalog';

interface ContactDetailsProps {
  selectedChat: Chat | null;
  onBack?: () => void;
}

const EmptyState: React.FC<{ 
  title: string; 
  description: string; 
  icon?: React.ReactNode;
  action?: React.ReactNode;
}> = ({ title, description, icon, action }) => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100%',
      textAlign: 'center',
      p: 4,
    }}
  >
    {icon && (
      <Box sx={{ mb: 2, color: 'text.secondary' }}>
        {icon}
      </Box>
    )}
    <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
      {title}
    </Typography>
    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
      {description}
    </Typography>
    {action}
  </Box>
);

const InfoField: React.FC<{
  label: string;
  value: string | null;
  icon?: React.ReactNode;
  isLink?: boolean;
  href?: string;
}> = ({ label, value, icon, isLink = false, href }) => {
  if (!value) return null;
  
  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
        {label}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {icon}
        {isLink && href ? (
          <Link href={href} target="_blank" rel="noopener noreferrer" sx={{ flex: 1 }}>
            <Typography variant="body2" sx={{ color: 'primary.main' }}>
              {value}
            </Typography>
          </Link>
        ) : (
          <Typography variant="body2" sx={{ flex: 1 }}>
            {value}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

const StatusChip: React.FC<{ status: string }> = ({ status }) => {
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'RUNNING':
        return 'success';
      case 'PAUSED':
        return 'warning';
      case 'DONE':
        return 'default';
      default:
        return 'default';
    }
  };

  return (
    <Chip
      label={status}
      color={getStatusColor(status) as any}
      size="small"
      sx={{ fontSize: '0.75rem' }}
    />
  );
};

// ─── Conversation Signals ────────────────────────────────────────────────────

const MATCH_STRENGTH_COLORS: Record<MatchStrength, string> = {
  STRONG:     'success',
  MODERATE:   'info',
  WEAK:       'warning',
  OFF_TARGET: 'error',
};

const ConversationSignalsSection: React.FC<{
  signals?: ConversationSignalEntry[];
}> = ({ signals }) => {
  const sorted = [...(signals ?? [])]
    .sort((a, b) => b.strength_value - a.strength_value)
    .slice(0, 3);
  if (sorted.length === 0) return null;

  const primary = sorted.find((s) => s.is_primary) ?? sorted[0];

  return (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          Conversation signals
        </Typography>
        {primary && (
          <Chip
            label={primary.strength_text.replace('_', '-')}
            size="small"
            color={MATCH_STRENGTH_COLORS[primary.strength_text] as any}
            sx={{ fontSize: '0.7rem', height: '20px' }}
          />
        )}
      </Box>
      <Stack spacing={1.25}>
        {sorted.map((s, idx) => {
          const ui = getConversationSignalUi(s.signal);
          const label = ui?.label ?? s.signal;
          const description = ui?.description ?? '';
          const dot = ui?.filter.dot ?? '#9e9e9e';
          const tip = [description, s.evidence ? `“${s.evidence}”` : ''].filter(Boolean).join(' — ');
          return (
            <Tooltip key={`${s.signal}-${idx}`} title={tip} placement="left" arrow>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    bgcolor: dot,
                    flexShrink: 0,
                    mt: 0.6,
                  }}
                />
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 600 }}>
                    {label}
                    {s.is_primary ? ' · primary' : ''}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.72rem', display: 'block' }}>
                    {s.strength_text.replace('_', '-')}
                    {s.evidence ? ` — ${s.evidence}` : ''}
                  </Typography>
                </Box>
              </Box>
            </Tooltip>
          );
        })}
      </Stack>
    </Box>
  );
};

// ─────────────────────────────────────────────────────────────────────────────

export const ContactDetails: React.FC<ContactDetailsProps> = ({ selectedChat }) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  
  const [isEditingNotes, setIsEditingNotes] = useState(false);
  const [notesValue, setNotesValue] = useState(selectedChat?.notes || '');

  const updateNotesMutation = useMutation({
    mutationFn: (notes: string) => 
      selectedChat ? chatApi.updateNotes(selectedChat.key, notes) : Promise.reject('No chat selected'),
    onSuccess: (updatedChat) => {
      setIsEditingNotes(false);
      // Update the local state with the new notes
      if (updatedChat && selectedChat) {
        const updatedSelectedChat = { ...selectedChat, notes: updatedChat.notes };
        // Update the query cache directly
        queryClient.setQueryData(['chats'], (oldData: any) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            output: {
              ...oldData.output,
              items: oldData.output.items.map((chat: Chat) => 
                chat.key === selectedChat.key ? updatedSelectedChat : chat
              )
            }
          };
        });
      }
      queryClient.invalidateQueries({ queryKey: ['chats'] });
      if (selectedChat) {
        queryClient.invalidateQueries({ queryKey: ['chat', selectedChat.key] });
      }
    },
    onError: (error) => {
      console.error('Failed to update notes:', error);
    },
  });

  const handleSaveNotes = useCallback(() => {
    if (selectedChat && notesValue !== selectedChat.notes) {
      updateNotesMutation.mutate(notesValue);
    } else {
      setIsEditingNotes(false);
    }
  }, [selectedChat, notesValue, updateNotesMutation]);

  const handleCancelNotes = useCallback(() => {
    setNotesValue(selectedChat?.notes || '');
    setIsEditingNotes(false);
  }, [selectedChat]);

  if (!selectedChat) {
    return (
      <EmptyState
        title="No contact selected"
        description="Click on a conversation to view contact details"
        icon={<BusinessIcon sx={{ fontSize: 48 }} />}
      />
    );
  }

  const prospect = selectedChat.prospect;
  const prospectName =
    prospect?.full_name ||
    (prospect?.first_name && prospect?.last_name
      ? `${prospect.first_name} ${prospect.last_name}`
      : prospect?.first_name ||
        prospect?.last_name ||
        'Unknown');
  const company = prospect.current_company;
  const stream = selectedChat.stream;

  return (
    <Box sx={{ 
      height: '100%', 
      maxHeight: '100%', 
      display: 'flex', 
      flexDirection: 'column', 
      overflow: 'hidden', 
      width: '100%', 
      minWidth: 0 
          }}>

      {/* Content */}
      <Box sx={{ p: 2, overflow: 'auto', flexGrow: 1, height: '100%' }}>
        {/* Profile Card */}
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Avatar
            src={prospect?.profile_image || undefined}
            sx={{
              width: 80,
              height: 80,
              bgcolor: 'primary.light',
              color: 'primary.main',
              fontSize: '2rem',
              mx: 'auto',
              mb: 2,
              cursor: 'pointer',
            }}
            onClick={() => prospect?.key && navigate(`/leads/${prospect.key}`)}
          >
            {prospectName.charAt(0).toUpperCase()}
          </Avatar>
          <Typography 
            variant="h6" 
            sx={{ fontWeight: 600, mb: 1, cursor: 'pointer', '&:hover': { color: 'primary.main', textDecoration: 'underline' } }}
            onClick={() => prospect?.key && navigate(`/leads/${prospect.key}`)}
          >
            {prospectName}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
            {prospect.title}
          </Typography>
          {company && (
            <Typography variant="body2" color="text.secondary">
              {company.name}
            </Typography>
          )}
        </Box>

        {/* Prospect Information */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
            Prospect Information
          </Typography>
          <Stack spacing={1}>
            <InfoField
              label="Email"
              value={prospect.email}
              icon={<EmailIcon sx={{ fontSize: 16, color: 'text.secondary' }} />}
            />
            <InfoField
              label="Phone"
              value={prospect.phone}
              icon={<PhoneIcon sx={{ fontSize: 16, color: 'text.secondary' }} />}
            />
            <InfoField
              label="Location"
              value={prospect.location}
              icon={<LocationIcon sx={{ fontSize: 16, color: 'text.secondary' }} />}
            />
            <InfoField
              label="LinkedIn"
              value={prospect.linkedin_public_url || prospect.linkedin_url}
              icon={<LinkedInIcon sx={{ fontSize: 16, color: 'text.secondary' }} />}
              isLink={true}
              href={prospect.linkedin_public_url || prospect.linkedin_url || undefined}
            />
            {prospect.summary && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                  Summary
                </Typography>
                <Typography variant="body2" sx={{ fontSize: '0.875rem', lineHeight: 1.5 }}>
                  {prospect.summary}
                </Typography>
              </Box>
            )}
            {prospect.duration_in_role && (
              <InfoField
                label="Duration in Role"
                value={prospect.duration_in_role}
                icon={<WorkIcon sx={{ fontSize: 16, color: 'text.secondary' }} />}
              />
            )}
            {prospect.duration_in_company && (
              <InfoField
                label="Duration in Company"
                value={prospect.duration_in_company}
                icon={<BusinessIcon sx={{ fontSize: 16, color: 'text.secondary' }} />}
              />
            )}
          </Stack>
        </Box>

        {/* Company Information */}
        {company && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
              Company Information
            </Typography>
            <Stack spacing={1}>
              <InfoField
                label="Company Name"
                value={company.name}
                icon={<BusinessIcon sx={{ fontSize: 16, color: 'text.secondary' }} />}
              />
              <InfoField
                label="Website"
                value={company.website}
                icon={<LanguageIcon sx={{ fontSize: 16, color: 'text.secondary' }} />}
                isLink={true}
                href={company.website || undefined}
              />
              <InfoField
                label="LinkedIn"
                value={company.linkedin_url}
                icon={<LinkedInIcon sx={{ fontSize: 16, color: 'text.secondary' }} />}
                isLink={true}
                href={company.linkedin_url || undefined}
              />
              <InfoField
                label="Industry"
                value={company.industry}
                icon={<BusinessIcon sx={{ fontSize: 16, color: 'text.secondary' }} />}
              />
              <InfoField
                label="Location"
                value={company.location}
                icon={<LocationIcon sx={{ fontSize: 16, color: 'text.secondary' }} />}
              />
              <InfoField
                label="Employee Count"
                value={company.employee_count}
                icon={<BusinessIcon sx={{ fontSize: 16, color: 'text.secondary' }} />}
              />
              {company.description && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    Description
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.875rem', lineHeight: 1.5 }}>
                    {company.description}
                  </Typography>
                </Box>
              )}
            </Stack>
          </Box>
        )}

        {/* Stream Information */}
        {stream && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
              Experiment Information
            </Typography>
            <Stack spacing={1}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {stream.name}
                </Typography>
                <StatusChip status={stream.status} />
              </Box>
              {stream.notes && (
                <Typography variant="body2" sx={{ fontSize: '0.875rem', lineHeight: 1.5 }}>
                  {stream.notes}
                </Typography>
              )}
            </Stack>
          </Box>
        )}

        {/* Conversation Signals Section */}
        <ConversationSignalsSection signals={selectedChat.conversation_signals} />

        <Divider sx={{ my: 3 }} />

        {/* Notes Section */}
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Notes
            </Typography>
            {!isEditingNotes ? (
              <IconButton 
                onClick={() => setIsEditingNotes(true)} 
                size="small"
                sx={{ color: 'primary.main' }}
              >
                <EditIcon />
              </IconButton>
            ) : (
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <IconButton 
                  onClick={handleSaveNotes} 
                  size="small"
                  disabled={updateNotesMutation.isPending}
                  sx={{ color: 'success.main' }}
                >
                  <SaveIcon />
                </IconButton>
                <IconButton 
                  onClick={handleCancelNotes} 
                  size="small"
                  sx={{ color: 'error.main' }}
                >
                  <CancelIcon />
                </IconButton>
              </Box>
            )}
          </Box>
          
          {isEditingNotes ? (
            <TextField
              multiline
              rows={4}
              value={notesValue}
              onChange={(e) => setNotesValue(e.target.value)}
              placeholder="Add notes about this contact..."
              fullWidth
              variant="outlined"
              size="small"
            />
          ) : (
            <Box>
              {selectedChat.notes ? (
                <Typography variant="body2" sx={{ lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                  {selectedChat.notes}
                </Typography>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  No notes yet. Click the edit button to add notes.
                </Typography>
              )}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}; 